// @ts-check
// Lista de conversas (coluna esquerda da caixa): filtros segmentados, filtros rápidos (responsável,
// não respondidas, sem card), busca e linhas com avatar, selo do canal, prévia da última mensagem,
// hora relativa (ou o tempo de espera por resposta), responsável e contador de não lidas.
import { html } from 'htm/preact';
import { Avatar } from '../../ui/avatar.js';
import { Icon } from '../../ui/icon.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { LightButton } from '../../ui/button.js';
import {
  AWAITING_REPLY_ALERT_MILLISECONDS,
  formatThreadListTime,
  formatWaitingDuration,
  getMessageKindIcon,
  getMessageKindLabel,
} from '../../lib/conversation-format.js';
import { ChannelIcon, getThreadDisplayName } from './conversation-display.js';
import { ALL_ASSIGNEES, AssigneeFilterButton, MemberAvatar } from './conversation-assignee.js';
import { DrilldownChip } from '../views/drilldown-chip.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/conversations.js').ConversationThread} ConversationThread */

export const THREAD_FILTERS = /** @type {const} */ (['all', 'whatsapp', 'email', 'unread']);

/**
 * @typedef {{ assignee: string, isAwaitingReplyOnly: boolean, isWithoutOpportunityOnly: boolean }} QuickFilters
 */

export const DEFAULT_QUICK_FILTERS = /** @type {QuickFilters} */ ({ assignee: ALL_ASSIGNEES, isAwaitingReplyOnly: false, isWithoutOpportunityOnly: false });

/** @param {QuickFilters} quickFilters */
export function hasActiveQuickFilters(quickFilters) {
  return quickFilters.assignee !== ALL_ASSIGNEES || quickFilters.isAwaitingReplyOnly || quickFilters.isWithoutOpportunityOnly;
}

/**
 * @param {{ label: string, isActive: boolean, onToggle: () => void }} props
 */
function QuickFilterToggle({ label, isActive, onToggle }) {
  return html`<button type="button" class="thread-quick-filter" data-active=${isActive ? 'true' : undefined} aria-pressed=${isActive ? 'true' : 'false'} onClick=${onToggle}>
    ${isActive ? html`<${Icon} name="IconCheck" size=${12} />` : null}
    <span class="thread-quick-filter-value">${label}</span>
  </button>`;
}

/**
 * @param {{ quickFilters: QuickFilters, onChange: (quickFilters: QuickFilters) => void }} props
 */
function QuickFilterBar({ quickFilters, onChange }) {
  return html`<div class="thread-quick-filters">
    <${AssigneeFilterButton} value=${quickFilters.assignee} onChange=${(assignee) => onChange({ ...quickFilters, assignee })} />
    <${QuickFilterToggle}
      label=${t('conversations.quickFilters.awaitingReply')}
      isActive=${quickFilters.isAwaitingReplyOnly}
      onToggle=${() => onChange({ ...quickFilters, isAwaitingReplyOnly: !quickFilters.isAwaitingReplyOnly })}
    />
    <${QuickFilterToggle}
      label=${t('conversations.quickFilters.withoutOpportunity')}
      isActive=${quickFilters.isWithoutOpportunityOnly}
      onToggle=${() => onChange({ ...quickFilters, isWithoutOpportunityOnly: !quickFilters.isWithoutOpportunityOnly })}
    />
  </div>`;
}

/**
 * Tempo esperando resposta da equipe, no lugar da hora da última mensagem (que é a mesma recebida);
 * ganha tom de alerta depois de uma hora.
 * @param {{ since: string | null | undefined }} props
 */
function AwaitingReplyIndicator({ since }) {
  const waiting = formatWaitingDuration(since);
  if (!waiting) return null;
  return html`<span
    class="thread-row-awaiting"
    data-alert=${waiting.milliseconds >= AWAITING_REPLY_ALERT_MILLISECONDS ? 'true' : undefined}
    title=${t('conversations.row.awaitingTitle', { duration: waiting.label })}
  >
    <${Icon} name="IconClock" size=${12} />
    <span class="thread-row-awaiting-full">${t('conversations.row.awaiting', { duration: waiting.label })}</span>
    <span class="thread-row-awaiting-short">${waiting.label}</span>
  </span>`;
}

/**
 * @param {{ thread: ConversationThread }} props
 */
function ThreadPreview({ thread }) {
  const lastMessage = thread.lastMessage;
  if (!lastMessage) return html`<span class="thread-row-preview-text"></span>`;
  // e-mail de conta conectada de um colega, com visibilidade restrita (contrato 08 da agenda)
  if (/** @type {any} */ (thread).isContentRestricted) {
    return html`<span class="thread-row-preview-text">
      <${Icon} name="IconLock" size=${14} className="thread-row-kind-icon" />
      ${thread.subject ? html`<span class="thread-row-subject">${thread.subject}</span> · ` : null}${t('calendar.notShared')}
    </span>`;
  }
  // 3.0.0: a última mensagem é de um atendimento restrito que o papel não vê (o servidor manda a marca, nunca o texto)
  if (thread.isLastMessageRestricted) {
    return html`<span class="thread-row-preview-text thread-row-restricted"><${Icon} name="IconLock" size=${14} className="thread-row-kind-icon" />${t('serviceDesk.thread.restrictedPreview')}</span>`;
  }
  const isOutgoing = lastMessage.direction === 'OUTGOING';
  const text = lastMessage.text?.trim();
  const kindIcon = getMessageKindIcon(lastMessage.kind);
  return html`<span class="thread-row-preview-text">
    ${isOutgoing ? html`<span class="thread-row-you">${t('conversations.you')} </span>` : null}
    ${kindIcon && !text ? html`<${Icon} name=${kindIcon} size=${14} className="thread-row-kind-icon" />` : null}
    ${thread.channelType === 'EMAIL' && thread.subject ? html`<span class="thread-row-subject">${thread.subject}</span>${text ? ' · ' : ''}` : null}
    ${text || (thread.channelType === 'EMAIL' && thread.subject ? '' : getMessageKindLabel(lastMessage.kind))}
  </span>`;
}

/**
 * @param {{ thread: ConversationThread, isSelected: boolean, onSelect: (thread: ConversationThread) => void }} props
 */
function ThreadRow({ thread, isSelected, onSelect }) {
  const displayName = getThreadDisplayName(thread);
  const hasUnread = thread.unreadCount > 0;
  const time = thread.lastMessage?.sentAt ?? thread.updatedAt;
  return html`<button
    type="button"
    class="thread-row"
    data-selected=${isSelected ? 'true' : undefined}
    data-unread=${hasUnread ? 'true' : undefined}
    aria-current=${isSelected ? 'true' : undefined}
    onClick=${() => onSelect(thread)}
  >
    <span class="thread-row-avatar">
      <${Avatar} placeholder=${displayName} seed=${thread.person?.id ?? thread.contact?.handle} size="xl" type="rounded" avatarUrl=${thread.person?.avatarUrl ?? null} />
      <${ChannelIcon} channelType=${thread.channelType} size=${16} className="thread-row-channel" />
    </span>
    <span class="thread-row-texts">
      <span class="thread-row-top">
        <span class="thread-row-name">${displayName}</span>
        ${thread.provisionalEnrollmentOpportunityId
          ? html`<span class="thread-row-new" title=${t('conversations.row.newTitle')}>${t('conversations.row.new')}</span>`
          : null}
        ${thread.isAwaitingReply
          ? html`<${AwaitingReplyIndicator} since=${thread.awaitingReplySince ?? thread.lastMessage?.sentAt} />`
          : html`<span class="thread-row-time" data-unread=${hasUnread ? 'true' : undefined}>${formatThreadListTime(time)}</span>`}
      </span>
      <span class="thread-row-bottom">
        <${ThreadPreview} thread=${thread} />
        ${hasUnread
          ? html`<span class="thread-row-unread" aria-label=${t('conversations.unreadCount', { count: thread.unreadCount })}>${thread.unreadCount}</span>`
          : null}
        ${thread.assignee
          ? html`<span class="thread-row-assignee" title=${t('conversations.row.assignedTo', { name: thread.assignee.name })}>
              <${MemberAvatar} name=${thread.assignee.name} seed=${thread.assignee.workspaceMemberId} />
            </span>`
          : null}
      </span>
    </span>
  </button>`;
}

/**
 * @param {{ threads: ConversationThread[] | null, selectedThreadId: string | null, onSelect: (thread: ConversationThread) => void,
 *   filter: string, onFilterChange: (filter: string) => void, searchInput: string, onSearchChange: (value: string) => void,
 *   quickFilters: QuickFilters, onQuickFiltersChange: (quickFilters: QuickFilters) => void, showQuickFilters?: boolean,
 *   hasNextPage: boolean, onLoadMore: () => void, isLoadingMore: boolean, emptyTitle?: string, emptySubtitle?: string,
 *   drilldown?: string | null, onClearDrilldown?: () => void }} props
 *   drilldown: caixa aberta por um clique num gráfico de conversas, com o chip "Do painel" no topo (contrato 07, seção 10)
 */
export function ThreadList({
  threads, selectedThreadId, onSelect, filter, onFilterChange, searchInput, onSearchChange, quickFilters, onQuickFiltersChange, showQuickFilters = true,
  hasNextPage, onLoadMore, isLoadingMore, emptyTitle, emptySubtitle, drilldown = null, onClearDrilldown,
}) {
  const hasOwnFilters = filter !== 'all' || searchInput.trim() !== '' || hasActiveQuickFilters(quickFilters);
  const isFiltered = hasOwnFilters || Boolean(drilldown);
  return html`<div class="thread-list">
    ${drilldown
      ? html`<div class="thread-list-drilldown">
          <${DrilldownChip} key=${drilldown} parameter=${drilldown} totalCount=${threads && !hasNextPage && !hasOwnFilters ? threads.length : null}
            onClear=${() => onClearDrilldown?.()} />
        </div>`
      : null}
    <div class="thread-list-toolbar">
      <div class="thread-list-filters segmented-control" role="tablist">
        ${THREAD_FILTERS.map((filterId) => html`<button
          type="button"
          key=${filterId}
          role="tab"
          class="segmented-item"
          aria-selected=${filter === filterId ? 'true' : 'false'}
          data-active=${filter === filterId ? 'true' : undefined}
          onClick=${() => onFilterChange(filterId)}
        >${t(`conversations.filters.${filterId}`)}</button>`)}
      </div>
      ${showQuickFilters ? html`<${QuickFilterBar} quickFilters=${quickFilters} onChange=${onQuickFiltersChange} />` : null}
      <label class="thread-list-search">
        <${Icon} name="IconSearch" size=${14} />
        <input
          type="search"
          value=${searchInput}
          placeholder=${t('conversations.searchPlaceholder')}
          aria-label=${t('conversations.searchPlaceholder')}
          onInput=${(event) => onSearchChange(/** @type {HTMLInputElement} */ (event.currentTarget).value)}
        />
      </label>
    </div>
    <div class="thread-list-items" role="list">
      ${threads === null
        ? [0, 1, 2, 3, 4].map((index) => html`<div key=${index} class="thread-row-skeleton">
            <${Skeleton} width=${40} height=${40} radius=${20} />
            <span class="thread-row-skeleton-texts"><${Skeleton} width="60%" height=${12} /><${Skeleton} width="90%" height=${12} /></span>
          </div>`)
        : threads.length === 0
          ? html`<div class="thread-list-empty">
              <${EmptyState}
                icon="IconMessages"
                title=${isFiltered ? t('conversations.emptyFilteredTitle') : emptyTitle ?? t('conversations.emptyListTitle')}
                subtitle=${isFiltered ? t('conversations.emptyFilteredSubtitle') : emptySubtitle ?? t('conversations.emptyListSubtitle')}
              />
            </div>`
          : threads.map((thread) => html`<${ThreadRow} key=${thread.id} thread=${thread} isSelected=${thread.id === selectedThreadId} onSelect=${onSelect} />`)}
      ${threads && hasNextPage
        ? html`<div class="thread-list-more"><${LightButton} title=${t('conversations.loadMore')} icon="IconChevronDown" disabled=${isLoadingMore} onClick=${onLoadMore} /></div>`
        : null}
    </div>
  </div>`;
}
